import { useHandler } from "@redotech/react-util/hook";
import { SegmentNumberOperator } from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import {
  rcoItemQuantitySegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { t, useTranslation } from "@redotech/redo-i18n";
import { OperatorSelector } from "@redotech/redo-web-conversion/conversion/operator-selector";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";

function segmentNumberOperatorToText(): Record<SegmentNumberOperator, string> {
  return {
    [SegmentNumberOperator.LESS_THAN]: t("less than"),
    [SegmentNumberOperator.LESS_THAN_OR_EQUAL]: t("less than or equal to"),
    [SegmentNumberOperator.EQUAL]: t("equal to"),
    [SegmentNumberOperator.GREATER_THAN]: t("greater than"),
    [SegmentNumberOperator.GREATER_THAN_OR_EQUAL]: t(
      "greater than or equal to",
    ),
  };
}

export const ItemQuantityInspectorContent = memo(
  function ItemQuantityInspectorContent({ step }: { step: RcoSegmentStep }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoItemQuantitySegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {t("Cart item quantity is")}{" "}
            {segmentNumberOperatorToText()[segment.operator]} {segment.quantity}
          </Text>
        </Flex>
        {segment.exclusions?.skus && segment.exclusions.skus.length > 0 && (
          <Flex align="flex-start" dir="column" gap="xs">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {t("Excluded SKUs")}
            </Text>
            <Flex dir="row" gap="md" wrap="wrap">
              {segment.exclusions.skus.map((sku, index) => (
                <RedoBadge color="gray" key={index} size="sm" text={sku} />
              ))}
            </Flex>
          </Flex>
        )}
      </>
    );
  },
);

export const EditableItemQuantityInspectorContent = memo(
  function EditableItemQuantityInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoItemQuantitySegmentSchema.parse(step.segment),
      [step],
    );

    const handleOperatorSelected = useHandler(
      (operator: SegmentNumberOperator) => {
        const updatedSegment = { ...segment, operator };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleQuantityChange = useHandler((quantity: number) => {
      const updatedSegment = { ...segment, quantity };
      setStep({ ...step, segment: updatedSegment });
    });

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {t("Cart item quantity is")}
          </Text>
        </Flex>
        <Flex>
          <OperatorSelector<SegmentNumberOperator>
            availableOptions={Object.values(SegmentNumberOperator)}
            onOperatorSelected={handleOperatorSelected}
            operatorToText={segmentNumberOperatorToText()}
            selectedOperator={segment.operator}
          />
        </Flex>
        <RedoIncrementDecrement
          min={0}
          setValue={handleQuantityChange}
          size="small"
          step={1}
          value={segment.quantity}
        />
        {segment.exclusions?.skus && segment.exclusions.skus.length > 0 && (
          <Flex align="flex-start" dir="column" gap="xs">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {t("Excluded SKUs")}
            </Text>
            <Flex dir="row" gap="md" wrap="wrap">
              {segment.exclusions.skus.map((sku, index) => (
                <RedoBadge color="gray" key={index} size="sm" text={sku} />
              ))}
            </Flex>
          </Flex>
        )}
      </>
    );
  },
);
